/**
 * Pure scene PLAN for the descriptor-driven scene builder (WR.3.3).
 *
 * `planScene()` turns the join-ack `WorldDescriptor` (+ the resolved asset
 * manifest and optional theme) into a `ScenePlan`: a plain-data description of
 * the world to render — background/fog, lights, ground, scatter props, the
 * shrine/transition/POI markers, and (dev-toggle-only) the collision-footprint
 * overlay. It imports NO `three` and does no I/O, so it runs under `node --test`
 * with no renderer (wr3-asset-pipeline-standup.md §12 risk 1 — "keep the
 * renderable seam thin"). The thin `SceneBuilder` (builder.ts) realizes a plan
 * into Three.js objects; THIS module is what the characterization test pins.
 *
 * Graceful degradation (spec §8): with no bound pack and no theme the plan is
 * the legacy placeholder world Game.ts rendered before WR.3.3 — same background,
 * fog, lights, ground, grid and six rocks, and the same WR.2 marker primitives.
 * A bound pack only UPGRADES: it marks props/markers with an `upgradeAssetId`
 * the builder instantiates over the placeholder, falling back to the placeholder
 * whenever the asset is absent or fails to load. Nothing here touches the
 * per-tick sim or the collision authority (AD-026/AD-047) — the collider overlay
 * is render-only debug viz, gated off by default.
 *
 * Import boundary (AD-122): shared content types come from the curated
 * `@great-cloud/shared/client` surface; `AssetManifest` is the framework-free
 * WR.3.2 index. No server barrel, no framework.
 */

import type {
  ClientGenerationSpec,
  CollisionShape,
  ThemeDef,
  WorldDescriptor,
  ZoneBounds,
  ZoneTier,
} from '@great-cloud/shared/client';
import {
  scatterInstances,
  hasAuthoredLandmark,
  settlementClearings,
  settlementPiecePlacements,
} from '@great-cloud/shared/client';

import { AssetManifest } from '../assets/manifest.js';
import { groundPaletteForBiome } from './groundPalette.js';
import {
  DEFAULT_CLOUD_PARAMS,
  DEFAULT_SKY_PARAMS,
  DEFAULT_SKY_RADIUS,
  skyHorizonHex,
  sunDirectionFromLight,
  type CloudParams,
  type SkyParams,
  type SkyVector,
} from './skyModel.js';
import { FLAT_GROUND, seatY, type GroundSurface, type HeightfieldPlan } from './terrain.js';
import { islandPlanOf, onIslandTop, type IslandPlan } from './island.js';

// ---------------------------------------------------------------------------
// Plan data model (plain data; builder.ts maps each field to a three object)
// ---------------------------------------------------------------------------

export interface FogPlan {
  color: string;
  near: number;
  far: number;
}

export interface HemisphereLightPlan {
  kind: 'hemisphere';
  sky: string;
  ground: string;
  intensity: number;
}
export interface DirectionalLightPlan {
  kind: 'directional';
  color: string;
  intensity: number;
  x: number;
  y: number;
  z: number;
}
/** Low ambient fill (VQ-001 / AD-194): lifts AgX-tone-mapped shadow floors. */
export interface AmbientLightPlan {
  kind: 'ambient';
  color: string;
  intensity: number;
}
export type LightPlan = HemisphereLightPlan | DirectionalLightPlan | AmbientLightPlan;

/**
 * The sky dome (AD-651 item 2): three's Preetham sky with a sun disc, drawn by
 * `Sky.js` on WebGL2 and `SkyMesh.js` on WebGPU from these same values. Plain data;
 * `skyModel.ts` holds the default and the maths.
 */
export interface SkyPlan extends SkyParams {
  /** Dome size in metres (the camera must stay inside it; depth is pinned to far). */
  radius: number;
  /** Unit direction TO the sun — derived from the plan's directional light. */
  sun: SkyVector;
  /**
   * The stylised weather-cloud layer and The Great Cloud-iridescence fringe (AD-9007; Art
   * Direction Bible §8.5), or null for a clear sky. three's own stock cloud term stays
   * forced OFF on both siblings; this is the replacement. The zone-tier seam: a theme may
   * later vary coverage / iridescence (nearer The Great Cloud's reach, stronger).
   */
  clouds: CloudParams | null;
}

export interface GridPlan {
  size: number;
  divisions: number;
  colorCenter: number;
  colorGrid: number;
  opacity: number;
  y: number;
}
/**
 * The ground surface. RECTANGULAR and positioned since AD-315: a zone whose
 * bounds are not centred on the origin (`ashenveil-depths` spans x ∈ [80, 240])
 * used to stand on a fixed 80×80 plane at (0,0) that its playable area did not
 * even intersect — the geometric void `q-empty-biomes-triage` measured. The
 * plane is now sized and centred on the zone's own `helloAck.bounds`; with no
 * bounds supplied it is the byte-exact legacy 80×80 plane at the origin.
 */
export interface GroundPlan {
  /** Centre of the plane (world units). Legacy placeholder: 0, 0. */
  x: number;
  z: number;
  sizeX: number;
  sizeZ: number;
  color: string;
  roughness: number;
  grid: GridPlan | null;
  /**
   * AD-408: the bound terrain tile set's heightfield. PRESENT ONLY when the zone
   * binds a set that covers its bounds — the key is absent (not null) otherwise,
   * so the flat AD-315 plan stays byte-identical. With a heightfield the builder
   * draws a vertex-normal-shaded mesh instead of the rectangle, and drapes the
   * SAME `grid` on it (`drapedGridArrays`) instead of a flat `GridHelper`, which
   * would slice through the relief.
   */
  heightfield?: HeightfieldPlan;
  /**
   * Procedural ground palette id (groundPalette.ts, 2026-09-14). PRESENT ONLY
   * when the joined biome binds one (`BIOME_GROUND_PALETTE`); absent ⇒ the
   * builder's default palette, so the placeholder plan stays byte-identical.
   */
  palette?: string;
}

/** A repeated scatter prop: a placeholder "rock", optionally upgraded to a pack asset. */
export interface PropPlan {
  x: number;
  y: number;
  z: number;
  /** Placeholder primitive (a dodecahedron rock) + its transform. */
  rock: { scale: number; rotationY: number };
  /** Instantiate this asset id over the placeholder when the pack provides it; else null. */
  upgradeAssetId: string | null;
}

export type MarkerKind = 'shrine' | 'transition' | 'poi';
export interface MarkerPlan {
  kind: MarkerKind;
  x: number;
  y: number;
  z: number;
  upgradeAssetId: string | null;
}

/**
 * A drawn piece of BIOME dressing (AD-315): one asset from a named pack at a
 * world transform, derived from the biome's authored `generation` in the client
 * content pack. Kept separate from {@link AssetPlacementPlan} because dressing
 * spans MULTIPLE packs (a settlement kit and a scatter kit are different packs),
 * while `assetPlacements` is the single-bound-pack showcase convention.
 */
export interface DressingPlan {
  assetPack: string;
  assetId: string;
  x: number;
  y: number;
  z: number;
  scale: number;
  rotationY: number;
  /** Settlement pieces carry their settlement id (diagnostics); scatter is null. */
  settlementId: string | null;
  /**
   * E3.4.S2 graceful fallback: the bound pack does NOT deliver `assetId`, so the
   * builder draws a placeholder primitive at THIS transform rather than nothing.
   *
   * Only settlement pieces are ever marked. The server projects each piece's
   * `colliderShapes` into the descriptor at exactly this transform
   * (`buildCollision`, one law — see `shared/src/content/scene-dressing.ts`), so
   * an undrawn piece is an INVISIBLE WALL: the player is blocked by geometry
   * that was never rendered. Scatter is visual-only and non-colliding, so an
   * undeliverable scatter asset is correctly dropped and is never marked here.
   */
  placeholder: boolean;
}

/** A render-only delivered asset placement, used for showcase/capital dressing. */
export interface AssetPlacementPlan {
  assetId: string;
  x: number;
  y: number;
  z: number;
  scale: number;
  rotationY: number;
}

/** A collision footprint reduced to renderable params (dev overlay only). */
export type ColliderPlan = (
  | { kind: 'circle'; x: number; z: number; radius: number }
  | { kind: 'aabb'; cx: number; cz: number; width: number; depth: number }
  | { kind: 'poly'; cx: number; cz: number; radius: number }
) & {
  /** AD-408: ground height under the footprint centre; absent on flat ground. */
  groundY?: number;
};

export interface ScenePlan {
  /** The clear colour — drawn only when `sky` is null (the dome covers it otherwise). */
  background: string;
  /** Distance fog; its colour is the sky's horizon whenever `sky` is present. */
  fog: FogPlan;
  /**
   * The sky dome, or null for the legacy flat background. `planScene` always sets the
   * stylised default; a theme may later supply its own values (the seam — no
   * per-biome skies yet).
   */
  sky: SkyPlan | null;
  lights: LightPlan[];
  ground: GroundPlan;
  props: PropPlan[];
  markers: MarkerPlan[];
  /** Render-only pack assets placed by client convention; never collision authority. */
  assetPlacements: AssetPlacementPlan[];
  /**
   * Biome dressing derived from the content pack (AD-315): settlement kit pieces
   * at the SHARED placement law's transforms + deterministic scatter. Empty
   * without `PlanOptions.dressing` (the pre-join placeholder world).
   */
  dressing: DressingPlan[];
  /** Empty unless the dev collider overlay is enabled. Render-only (AD-047). */
  colliderOverlay: ColliderPlan[];
  /**
   * AD-840: the zone drawn as a floating island (top cut to the coastline's rim,
   * underside, crags, cloud sea, distant islands), or null for the flat rectangle.
   * Present iff the join descriptor carries collision.outline + island. On a
   * heightfield zone the top is the relief cut to the rim (q-island-edges-heightfield).
   * With it the builder draws no rectangle and no grid.
   */
  island: IslandPlan | null;
  /** The bound pack id (diagnostics), or null in placeholder-fallback mode. */
  packId: string | null;
}

/**
 * Everything the plan needs to derive biome dressing from the client content
 * pack (AD-315). Supplied by Game.ts once the join-ack and the pack are both in
 * hand; absent before then, so the placeholder world is unchanged.
 */
export interface DressingOptions {
  /** The zone's own bounds from `helloAck.bounds` — sizes/positions the ground. */
  bounds: ZoneBounds;
  /** The joined zone's biome id (the deterministic scatter seed). */
  biomeId: string;
  /** The biome's client-pack `generation` sub-projection, or null when absent. */
  generation: ClientGenerationSpec | null;
  /** The zone's tier (client pack) — its scatter population weight; absent ⇒ authored density. */
  tier?: ZoneTier | null;
  /** Asset ids a pack provides, in pack order; unknown pack ⇒ `[]`. */
  packAssets: (assetPack: string) => readonly string[];
  /**
   * AD-408: the ground surface derived from the biome's bound terrain set
   * (`buildGroundSurface`). Absent/null ⇒ flat ground, today's plan exactly.
   * Game builds it once per join and seats its live entities on the SAME
   * surface, so the scene and the avatars can never disagree.
   */
  ground?: GroundSurface | null;
}

export interface PlanOptions {
  /** Render the collision footprints as a translucent debug overlay (default false). */
  colliderOverlay?: boolean;
  /** Biome dressing inputs (AD-315). Absent ⇒ no dressing and the legacy ground. */
  dressing?: DressingOptions | null;
}

// ---------------------------------------------------------------------------
// Legacy placeholder constants (the exact pre-WR.3.3 Game.buildWorld values)
// ---------------------------------------------------------------------------

const PLACEHOLDER_BACKGROUND = '#0b1020';
/** Legacy fog distances (the colour now comes from the sky's horizon — AD-651 item 2). */
const PLACEHOLDER_FOG_RANGE = { near: 45, far: 120 } as const;

const PLACEHOLDER_GROUND: GroundPlan = {
  x: 0,
  z: 0,
  sizeX: 80,
  sizeZ: 80,
  color: '#1d2c42',
  roughness: 1,
  grid: {
    size: 80,
    divisions: 40,
    colorCenter: 0x33507a,
    colorGrid: 0x22344f,
    opacity: 0.5,
    y: 0.01,
  },
};

/** AD-840: a scatter instance's footprint radius per unit of scale, kept on the island top. */
const ISLAND_SCATTER_INSET_M = 0.6;

/** The legacy grid's spacing (80 / 40). Preserved when the ground follows bounds. */
const GRID_SPACING = 2;

/** Ceiling on grid lines so a large zone cannot spawn a pathological GridHelper. */
const GRID_MAX_DIVISIONS = 120;

/**
 * The six placeholder rocks: [x, z, scale]. The coordinates are authored in the
 * {@link PLACEHOLDER_GROUND} frame (80×80 on the origin) and are mapped onto
 * whatever ground the zone actually gets by {@link placeholderRockPlacement} —
 * they are NOT absolute world metres. Authoring them as metres is what put them
 * over the void in an off-origin zone (`q-plaza-scatter-rocks-sit-off-the-deck`).
 */
/**
 * The placeholder dodecahedron's centre height per unit of `rock.scale`: a {@link PropPlan}'s
 * `y` is the ground PLUS this lift, because the primitive is drawn about its centre.
 */
export const PLACEHOLDER_ROCK_CENTRE_LIFT = 0.6;

/**
 * The ground under a scatter prop — its `y` without the placeholder's centre lift. A pack
 * asset upgraded over the placeholder seats its BASE here (`seatTexturedPlacement` reads
 * `placement.y` as the ground); handing it `prop.y` floated every upgraded rock by
 * `scale × 0.6` — 1.2 m for the largest (`q-o071-playtest-fixes` (c), owner playtest O-071).
 */
export function propGroundY(prop: PropPlan): number {
  return prop.y - prop.rock.scale * PLACEHOLDER_ROCK_CENTRE_LIFT;
}

const PLACEHOLDER_ROCKS: ReadonlyArray<readonly [number, number, number]> = [
  [-12, 6, 1.4],
  [14, -9, 1.0],
  [-20, -16, 2.0],
  [22, 18, 1.6],
  [4, 24, 1.2],
  [-28, 20, 1.1],
];

/**
 * Marker base heights (placeholder primitives sit at these y). The shrine cone
 * FLOATS as a beacon (cone base ≈ 2.6 — above heads and the y=2.1 health bars):
 * shrine loci are exactly where characters stand (zone spawn points, seed
 * checkpoints — the techdemo envoy's Nimbus (0,0) IS the Cloudspire — and future
 * shrine travel), and a ground-seated 2.2 m cone encloses the player rig (owner
 * report 2026-07-11: rigged player filmed inside the spawn shrine). Floating it
 * keeps the landmark while characters and captures stay clear. NOTE: the marker
 * upgrade path seats a delivered pack asset at this same y — no committed pack
 * delivers a marker upgrade id today, and the AP4 ENV-026 real-shrine
 * integration reconciles this seat back to ground level (P3 note, TASKS
 * 2026-07-11).
 */
export const MARKER_Y: Readonly<Record<MarkerKind, number>> = {
  shrine: 3.7,
  transition: 1.2,
  poi: 0.8,
};

/**
 * Convention asset ids a delivered pack may provide to UPGRADE the placeholder
 * scatter/markers. A pack that omits an id simply keeps the placeholder (the
 * Driftwell kit provides `rock-cluster`, so its rocks upgrade while its shrines
 * stay primitives). These are presentation conventions, not a frozen schema.
 */
export const SCATTER_ROCK_ASSET = 'rock-cluster';
export const MARKER_UPGRADE_ASSET: Readonly<Record<MarkerKind, string>> = {
  shrine: 'shrine',
  transition: 'transition-gate',
  poi: 'poi-marker',
};

/**
 * **The Nimbus showcase table is RETIRED (E3.4.S3).** It held twelve client-local,
 * visual-only placements anchored on the Cloudspire shrine — the interim stand-in
 * for "the full collision-bearing settlement kit-bash remains the deferred
 * E3.4/Phase-11 path". That path has now landed: `nimbus.json`'s `nimbus-capital`
 * settlement authors those same twelve pieces (and 201 more) as DESCRIPTOR content
 * at the same coordinates, so they arrive through the shared placement law
 * (`settlementPiecePlacements`) as dressing, with server colliders projected at the
 * identical transforms. Keeping the table would have drawn the capital core TWICE,
 * once solid and once not.
 *
 * The showcase's own authoring note is preserved where it is still load-bearing:
 * the descriptor pieces carry NO `scale` (`StructurePiece` has none), so they are
 * the world-scale `lods[0]` at scale 1 — exactly what the retired table's comment
 * said those artefacts are. The table's two `scale: 1.5` entries (`civic-plaza`,
 * `cloudspire`) could not cross into the descriptor and were dropped to 1; adding a
 * scale field would be a T3 escalation out of the feature, not an authoring choice.
 *
 * `AssetPlacementPlan`, the `assetPlacements` plan field, the builder's textured
 * path and {@link seatTexturedPlacement} are all DELIBERATELY KEPT. They are the
 * general single-bound-pack convention and the owner-gated M0 textured-capture
 * decision hangs off them; this story retired a table, not a mechanism.
 *
 * ⛔ The ENV-026 `MARKER_Y` shrine reconciliation the S3 stub allows for does NOT
 * execute here, and that is a measurement not an omission: the descriptor seats
 * `cloudspire` at (0, -22) while the `nimbus-cloudspire` shrine marker is at
 * (0, 0) — the spawn point, which the layout deliberately keeps clear. No real
 * shrine piece seats on the marker, so there is nothing to reconcile.
 */
function assetPlacements(): AssetPlacementPlan[] {
  return [];
}

/**
 * A minimal axis-aligned bounding box (world units) — the measured extent of a
 * loaded template. Kept `three`-free so the seating maths stays node-testable.
 */
export interface BoxDims {
  minX: number;
  minY: number;
  minZ: number;
  maxX: number;
  maxY: number;
  maxZ: number;
}

/** The per-axis scale + world position to seat a textured render at a placement. */
export interface TexturedSeat {
  scaleX: number;
  scaleY: number;
  scaleZ: number;
  x: number;
  y: number;
  z: number;
}

/**
 * A tiny lift (world units) so a seated base sits just above the ground plane
 * rather than exactly coplanar with it — belt-and-braces with the ground's
 * polygonOffset against the "bases flicker in the ground" z-fighting.
 */
export const TEXTURED_GROUND_EPSILON = 0.01;

/**
 * Seat a normalized textured render (`tex` — the entry's base `.glb`, modeled
 * ≈[-1,1] and centred at the origin) so it occupies the SAME world footprint as
 * the correctly-placed reference proxy (`ref` — the world-scale `lods[0]` the
 * showcase table was authored against), with its base on the ground.
 *
 * Per-axis scale maps the textured box onto the reference box (so proportions
 * come out right no matter how the render was normalized), times the placement's
 * own scalar. The mesh is then centred on the placement's x/z and its base
 * dropped to `placement.y` (+ epsilon). This reuses the already-correct proxy
 * seating instead of re-deriving per-asset y/scale by hand — the capture-time
 * textured-showcase decision (M0). Pure — no `three`; unit-tested in plan.test.
 */
export function seatTexturedPlacement(
  tex: BoxDims,
  ref: BoxDims,
  placement: { x: number; y: number; z: number; scale: number },
): TexturedSeat {
  const EPS = 1e-6;
  const texSizeX = Math.max(tex.maxX - tex.minX, EPS);
  const texSizeY = Math.max(tex.maxY - tex.minY, EPS);
  const texSizeZ = Math.max(tex.maxZ - tex.minZ, EPS);
  const refSizeX = Math.max(ref.maxX - ref.minX, EPS);
  const refSizeY = Math.max(ref.maxY - ref.minY, EPS);
  const refSizeZ = Math.max(ref.maxZ - ref.minZ, EPS);

  const scaleX = (refSizeX / texSizeX) * placement.scale;
  const scaleY = (refSizeY / texSizeY) * placement.scale;
  const scaleZ = (refSizeZ / texSizeZ) * placement.scale;

  const texCenterX = (tex.minX + tex.maxX) / 2;
  const texCenterZ = (tex.minZ + tex.maxZ) / 2;

  return {
    scaleX,
    scaleY,
    scaleZ,
    x: placement.x - texCenterX * scaleX,
    y: placement.y - tex.minY * scaleY + TEXTURED_GROUND_EPSILON,
    z: placement.z - texCenterZ * scaleZ,
  };
}

/** The empty descriptor used for the pre-join placeholder world. */
export const EMPTY_DESCRIPTOR: WorldDescriptor = {
  collision: { shapes: [] },
  shrines: [],
  transitions: [],
  pointsOfInterest: [],
};

const HEX_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

// ---------------------------------------------------------------------------
// Planning
// ---------------------------------------------------------------------------

/**
 * The theme tint applied in WR.3.3: the first valid hex in `theme.palette`,
 * applied to the background + fog colour. A null/absent theme, or a palette with
 * no hex, keeps the placeholder colour. Richer lighting/skybox/weather profiles
 * are reserved until the art direction defines their vocabulary (§8; WR.3.4+).
 */
export function themeTint(theme?: ThemeDef | null): string | null {
  if (!theme || !Array.isArray(theme.palette)) return null;
  for (const entry of theme.palette) {
    if (typeof entry === 'string' && HEX_RE.test(entry)) return entry;
  }
  return null;
}

/**
 * The physically-calibrated light rig (VQ-001 / AD-194 §1): a warm directional
 * sun (the only shadow caster), a hemisphere sky fill, and a LOW cool ambient
 * floor. Intensities are calibrated for AgX tone mapping (whose highlight
 * rolloff reads darker than the legacy no-tone-mapping output the previous
 * 1.0/1.4 pair was tuned against).
 */
function placeholderLights(): LightPlan[] {
  return [
    { kind: 'hemisphere', sky: '#bcd4ff', ground: '#1b2438', intensity: 1.25 },
    // AD-9005 (owner, 2026-09-25): a LOW sun (≈16.7° elevation, same bearing as the AD-194
    // rig's 54° sun) so the sky dome's disc sits inside the orbit camera's reach, with warmer,
    // longer light. Intensity re-balanced for the shallower angle on flat ground.
    { kind: 'directional', color: '#ffe6c8', intensity: 4.2, x: 72, y: 26, z: 48 },
    { kind: 'ambient', color: '#334155', intensity: 0.2 },
  ];
}

/**
 * The default sky for a light rig: the stylised parameters, the sun taken from the
 * rig's directional light (so the disc sits where the shadows point), no clouds.
 * A rig with no directional light keeps the model's default high sun.
 */
export function defaultSky(lights: readonly LightPlan[]): SkyPlan {
  const sunLight = lights.find((l): l is DirectionalLightPlan => l.kind === 'directional');
  return {
    ...DEFAULT_SKY_PARAMS,
    radius: DEFAULT_SKY_RADIUS,
    sun: sunDirectionFromLight(sunLight ?? { x: 0, y: 1, z: 0 }),
    clouds: DEFAULT_CLOUD_PARAMS,
  };
}

function placeholderGround(): GroundPlan {
  return {
    x: PLACEHOLDER_GROUND.x,
    z: PLACEHOLDER_GROUND.z,
    sizeX: PLACEHOLDER_GROUND.sizeX,
    sizeZ: PLACEHOLDER_GROUND.sizeZ,
    color: PLACEHOLDER_GROUND.color,
    roughness: PLACEHOLDER_GROUND.roughness,
    grid: PLACEHOLDER_GROUND.grid ? { ...PLACEHOLDER_GROUND.grid } : null,
  };
}

/**
 * The ground plane for a zone with known bounds (AD-315): sized to the bounds
 * and centred on them, so an off-origin zone stands on ground instead of over a
 * void. Degenerate bounds (zero/negative span — a malformed ack) fall back to
 * the placeholder rather than planning an invalid geometry.
 *
 * The grid keeps the legacy 2 m spacing. `THREE.GridHelper` is square by
 * construction, so it covers the LONGER span at that spacing (capped at
 * {@link GRID_MAX_DIVISIONS} lines for a very large zone) rather than scaling
 * per-axis — uniform spacing is what "the legacy grid, over the real zone"
 * actually means for a single helper.
 */
function boundsGround(bounds: ZoneBounds, surface: GroundSurface, biomeId: string): GroundPlan {
  const sizeX = bounds.maxX - bounds.minX;
  const sizeZ = bounds.maxZ - bounds.minZ;
  if (!(sizeX > 0) || !(sizeZ > 0)) return placeholderGround();
  const gridSize = Math.max(sizeX, sizeZ);
  const divisions = Math.max(1, Math.min(GRID_MAX_DIVISIONS, Math.round(gridSize / GRID_SPACING)));
  const legacyGrid = PLACEHOLDER_GROUND.grid;
  const palette = groundPaletteForBiome(biomeId);
  return {
    x: (bounds.minX + bounds.maxX) / 2,
    z: (bounds.minZ + bounds.maxZ) / 2,
    sizeX,
    sizeZ,
    color: PLACEHOLDER_GROUND.color,
    roughness: PLACEHOLDER_GROUND.roughness,
    // sprint-assembly-1 (AD-647): a zone whose ground is a painted palette texture draws NO
    // grid. The 2 m lines were the scale/motion reference for a flat-coloured plane; the
    // texture is that reference now, and over packed earth the lines read as a test floor
    // (the owner's "flat plane" playtest). An unpaletted zone keeps the legacy grid.
    grid: palette === null && legacyGrid ? { ...legacyGrid, size: gridSize, divisions } : null,
    // AD-408: key present ONLY with relief, so the flat plan stays byte-identical.
    ...(surface.heightfield !== null ? { heightfield: surface.heightfield } : {}),
    // Procedural ground palette: key present ONLY for a biome that binds one.
    ...(palette !== null ? { palette } : {}),
  };
}

/**
 * Place one authored placeholder rock on the ground the zone actually got
 * (AD-315's law, extended from the plane to the props that stand on it): the
 * authored coordinate is read as a FRACTION of the placeholder ground and
 * re-expressed in the real ground's frame, so the six rocks keep their relative
 * layout and stay on the plane wherever that plane is centred and however it is
 * sized. Identity on the placeholder ground itself, which is why the legacy
 * scene is byte-for-byte unchanged. `scale` is deliberately NOT mapped — a
 * boulder is a world-sized object, not a fraction of the zone.
 */
function placeholderRockPlacement(
  x: number,
  z: number,
  ground: GroundPlan,
): { x: number; z: number } {
  return {
    x: ground.x + x * (ground.sizeX / PLACEHOLDER_GROUND.sizeX),
    z: ground.z + z * (ground.sizeZ / PLACEHOLDER_GROUND.sizeZ),
  };
}

/**
 * Derive the biome dressing: settlement kit pieces at the SHARED placement law's
 * transforms (the same call `buildCollision` makes server-side — one law, so
 * visual⇄collider parity is structural), then the deterministic scatter, which
 * is rejected against the descriptor's own collision and the fixed markers.
 * A SETTLEMENT piece whose pack delivers no such asset is kept and marked
 * `placeholder` (E3.4.S2): the server blocks movement at that same transform, so
 * dropping it would leave an invisible wall. Scatter is visual-only, so an
 * undeliverable scatter asset is dropped by `scatterInstances` as before.
 */
function dressingPlan(
  descriptor: WorldDescriptor,
  dressing: DressingOptions,
  ground: GroundSurface,
  island: IslandPlan | null,
): DressingPlan[] {
  const generation = dressing.generation;
  if (generation === null) return [];
  const out: DressingPlan[] = [];
  const delivered = new Map<string, ReadonlySet<string>>();
  const packDelivers = (packId: string, assetId: string): boolean => {
    let ids = delivered.get(packId);
    if (ids === undefined) {
      ids = new Set(dressing.packAssets(packId));
      delivered.set(packId, ids);
    }
    return ids.has(assetId);
  };
  for (const placement of settlementPiecePlacements(generation.settlements)) {
    out.push({
      assetPack: placement.assetPack,
      assetId: placement.asset,
      x: placement.x,
      y: seatY(ground, placement.x, placement.z, placement.y),
      z: placement.z,
      scale: 1,
      rotationY: placement.rotation,
      settlementId: placement.settlementId,
      placeholder: !packDelivers(placement.assetPack, placement.asset),
    });
  }
  const markers = [
    ...descriptor.shrines.map((s) => ({ x: s.x, z: s.z })),
    ...descriptor.transitions.map((t) => ({ x: t.x, z: t.z })),
    ...descriptor.pointsOfInterest.map((p) => ({ x: p.x, z: p.z })),
  ];
  for (const instance of scatterInstances({
    biomeId: dressing.biomeId,
    rules: generation.scatter,
    bounds: dressing.bounds,
    collision: descriptor.collision.shapes,
    markers,
    clearings: settlementClearings(generation.settlements),
    packAssets: dressing.packAssets,
    tier: dressing.tier ?? null,
  })) {
    // AD-840: scatter is generated over the bounds rectangle; on an island, what
    // would stand past the rim (over the cloud sea) is not drawn.
    if (
      island &&
      !onIslandTop(island, instance.x, instance.z, ISLAND_SCATTER_INSET_M * instance.scale + 0.3)
    ) {
      continue;
    }
    out.push({
      assetPack: instance.assetPack,
      assetId: instance.assetId,
      x: instance.x,
      y: seatY(ground, instance.x, instance.z, instance.y),
      z: instance.z,
      scale: instance.scale,
      rotationY: instance.rotationY,
      settlementId: null,
      // Scatter is non-colliding: `scatterInstances` already emits only assets
      // the pack delivers, so scatter is never a placeholder (see DressingPlan).
      placeholder: false,
    });
  }
  return out;
}

/** Reduce a collision footprint to its renderable overlay params (matches the WR.2 mesh math). */
function colliderPlan(shape: CollisionShape, ground: GroundSurface): ColliderPlan {
  const plan = colliderFootprint(shape);
  if (ground.heightfield === null) return plan;
  const cx = plan.kind === 'circle' ? plan.x : plan.cx;
  const cz = plan.kind === 'circle' ? plan.z : plan.cz;
  return { ...plan, groundY: seatY(ground, cx, cz, 0) };
}

function colliderFootprint(shape: CollisionShape): ColliderPlan {
  if (shape.kind === 'circle') {
    return { kind: 'circle', x: shape.x, z: shape.z, radius: shape.radius };
  }
  if (shape.kind === 'aabb') {
    return {
      kind: 'aabb',
      cx: (shape.minX + shape.maxX) / 2,
      cz: (shape.minZ + shape.maxZ) / 2,
      width: shape.maxX - shape.minX,
      depth: shape.maxZ - shape.minZ,
    };
  }
  // poly: centroid + max vertex radius (the WR.2 approximation).
  let cx = 0;
  let cz = 0;
  for (const pt of shape.points) {
    cx += pt.x;
    cz += pt.z;
  }
  const n = shape.points.length || 1;
  cx /= n;
  cz /= n;
  let radius = 0;
  for (const pt of shape.points) radius = Math.max(radius, Math.hypot(pt.x - cx, pt.z - cz));
  return { kind: 'poly', cx, cz, radius };
}

function markerPlan(
  kind: MarkerKind,
  x: number,
  z: number,
  manifest: AssetManifest,
  ground: GroundSurface,
): MarkerPlan {
  const id = MARKER_UPGRADE_ASSET[kind];
  return {
    kind,
    x,
    y: seatY(ground, x, z, MARKER_Y[kind]),
    z,
    upgradeAssetId: manifest.bound && manifest.has(id) ? id : null,
  };
}

/**
 * Plan the scene for a join descriptor. Pure: same inputs → same plan. With an
 * unbound manifest and no theme it is the exact legacy placeholder; a bound pack
 * only adds `upgradeAssetId`s (graceful — the builder keeps the placeholder when
 * an asset is missing or fails to load).
 */
export function planScene(
  descriptor: WorldDescriptor,
  manifest: AssetManifest,
  theme?: ThemeDef | null,
  options?: PlanOptions,
): ScenePlan {
  const tint = themeTint(theme);
  const lights = placeholderLights();
  const sky = defaultSky(lights);
  // AD-651 item 2: the fog is the sky's own horizon, so distant geometry fades into
  // the colour the dome draws behind it (the theme tint stays the fallback clear colour).
  const fog: FogPlan = {
    color: skyHorizonHex(sky.sun, sky),
    near: PLACEHOLDER_FOG_RANGE.near,
    far: PLACEHOLDER_FOG_RANGE.far,
  };

  // The ground is derived BEFORE the props, because the props stand on it.
  const dressing = options?.dressing ?? null;
  // AD-408: one surface seats EVERY world-space category below (flat when unbound).
  const surface = dressing?.ground ?? FLAT_GROUND;
  const outline = descriptor.collision.outline;
  const island =
    dressing && outline && descriptor.island ? islandPlanOf(outline, descriptor.island) : null;
  const bounded = dressing
    ? boundsGround(dressing.bounds, surface, dressing.biomeId)
    : placeholderGround();
  // An island draws no grid: the square GridHelper would hang over the cloud sea.
  const ground = island ? { ...bounded, grid: null } : bounded;

  const rockUpgrade =
    manifest.bound && manifest.has(SCATTER_ROCK_ASSET) ? SCATTER_ROCK_ASSET : null;
  // An authored yard (sprint-assembly-1, AD-647) keeps the legacy rocks out exactly as
  // it keeps scatter out; a zone with no clearing keeps all six, byte-identical.
  const clearings = settlementClearings(dressing?.generation?.settlements);
  const inClearing = (px: number, pz: number): boolean =>
    clearings.some((c) => Math.hypot(px - c.x, pz - c.z) < c.radius);
  const props: PropPlan[] = PLACEHOLDER_ROCKS.flatMap(([x, z, scale]) => {
    const placed = placeholderRockPlacement(x, z, ground);
    if (inClearing(placed.x, placed.z)) return [];
    if (island && !onIslandTop(island, placed.x, placed.z, scale * 1.2)) return [];
    return {
      x: placed.x,
      y: seatY(surface, placed.x, placed.z, scale * PLACEHOLDER_ROCK_CENTRE_LIFT),
      z: placed.z,
      // Rotation stays a function of the AUTHORED x, so a given rock keeps its
      // facing in every zone rather than swinging with the ground it lands on.
      rock: { scale, rotationY: x * 0.7 },
      upgradeAssetId: rockUpgrade,
    };
  });

  // q-player-names-and-wayfinding: a shrine with an authored landmark piece IS that piece (the
  // Reach's wayside shrine, Driftwell's well); the WR.2 cone is scaffolding for undressed zones
  // only. The content validator requires the landmark in every dressed zone.
  const authoredPieces = settlementPiecePlacements(dressing?.generation?.settlements);
  const markers: MarkerPlan[] = [
    ...descriptor.shrines
      .filter((s) => !hasAuthoredLandmark(authoredPieces, s.x, s.z))
      .map((s) => markerPlan('shrine', s.x, s.z, manifest, surface)),
    ...descriptor.transitions.map((t) => markerPlan('transition', t.x, t.z, manifest, surface)),
    ...descriptor.pointsOfInterest.map((p) => markerPlan('poi', p.x, p.z, manifest, surface)),
  ];

  const colliderOverlay: ColliderPlan[] = options?.colliderOverlay
    ? descriptor.collision.shapes.map((shape) => colliderPlan(shape, surface))
    : [];

  return {
    background: tint ?? PLACEHOLDER_BACKGROUND,
    fog,
    sky,
    lights,
    ground,
    props,
    markers,
    assetPlacements: assetPlacements(),
    dressing: dressing ? dressingPlan(descriptor, dressing, surface, island) : [],
    colliderOverlay,
    island,
    packId: manifest.packId,
  };
}
